import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useAppContext } from '../hooks/AppContextProvider';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

// Constant for localStorage key
export const VIEW_TENANT_KEY = 'isViewTenant';

// Tạo interface cho context
interface ViewTenantContextType {
  viewTenantDomain: string | null;
  isViewTenantMode: boolean;
  enterViewTenantMode: (domain: string) => void;
  exitViewTenantMode: () => void;
}

// Tạo context với giá trị mặc định
const ViewTenantContext = createContext<ViewTenantContextType>({
  viewTenantDomain: null,
  isViewTenantMode: false,
  enterViewTenantMode: () => { },
  exitViewTenantMode: () => { },
});

// Routes where view tenant mode is allowed to be displayed
const ALLOWED_ROUTES = [
  '/tenant-domains',
  '/api-request-history',
  '/product-management',
  '/inspection-item-list',
  '/received-products',
  '/',
];

// URL patterns for dynamic routes
const DYNAMIC_ROUTE_PATTERNS = [
  /^\/[^/]+\/[^/]+\/received-products/,
  /^\/[^/]+\/[^/]+$/,
  /^\/received-products/,
  /^\/user\/[^/]+\/[^/]+\/received-products/,
];

interface ViewTenantProviderProps {
  children: ReactNode;
}

export const ViewTenantProvider: React.FC<ViewTenantProviderProps> = ({ children }) => {
  const [viewTenantDomain, setViewTenantDomain] = useState<string | null>(null);
  const { setHeaderCenterMessage } = useAppContext();
  const { t } = useTranslation();
  const location = useLocation();

  const isViewTenantMode = Boolean(viewTenantDomain);

  const isAllowedRoute = () => {
    const path = window.location.pathname;
    
    for (const pattern of DYNAMIC_ROUTE_PATTERNS) {
      if (path.match(pattern)) {
        return true;
      }
    }
    const isAllowed = ALLOWED_ROUTES.some(route => path.startsWith(route));    
    return isAllowed;
  };

  // Enter view tenant mode
  const enterViewTenantMode = (domain: string) => {
    localStorage.setItem(VIEW_TENANT_KEY, domain);
    setViewTenantDomain(domain);
  };

  // Exit view tenant mode
  const exitViewTenantMode = () => {
    localStorage.removeItem(VIEW_TENANT_KEY);
    setViewTenantDomain(null);
    setHeaderCenterMessage('');
  };

  // Load view tenant domain from localStorage on mount
  useEffect(() => {
    const storedViewTenant = localStorage.getItem(VIEW_TENANT_KEY);
    if (storedViewTenant && isAllowedRoute()) {
      setViewTenantDomain(storedViewTenant);
    }
  }, []);

  // Update header message when viewTenantDomain changes or location changes
  useEffect(() => {
    if (viewTenantDomain && isAllowedRoute()) {
      setHeaderCenterMessage(`${t('common.viewing_tenant_mode', { domain: viewTenantDomain })}`);
    } else if (viewTenantDomain) {
      setHeaderCenterMessage('');
    } else {
      setHeaderCenterMessage('');
    }

    return () => {
      if (viewTenantDomain) {
        setHeaderCenterMessage('');
      }
    };
  }, [viewTenantDomain, setHeaderCenterMessage, t, location]);

  return (
    <ViewTenantContext.Provider
      value={{
        viewTenantDomain,
        isViewTenantMode,
        enterViewTenantMode,
        exitViewTenantMode
      }}
    >
      {children}
    </ViewTenantContext.Provider>
  );
};

// Custom hook to use the ViewTenant context
export const useViewTenant = () => useContext(ViewTenantContext); 